import { request } from '@/utils/http'

// ===================== 类型定义 =====================

/**
 * 运踪配置查询请求参数
 */
export interface TrackingConfigQueryRequest {
  /** 渠道编码 */
  channelCode?: string
}

/**
 * 渠道请求参数
 */
export interface TrackingChannelRequest {
  /** 渠道ID（更新时必填，新增时不填） */
  id?: string
  /** 渠道编码 */
  code: string
  /** 渠道中文名称 */
  nameCn: string
  /** 渠道英文名称 */
  nameEn?: string
  /** 渠道类型：0-主渠道，1-第三方渠道 */
  type: number
  /** 启用状态：0-禁用，1-启用（默认1） */
  enable?: number
}

/**
 * 业务请求参数
 */
export interface TrackingBusinessRequest {
  /** 业务ID（更新时必填，新增时不填） */
  id?: string
  /** 业务编码 */
  code: string
  /** 业务中文名称 */
  nameCn: string
  /** 业务英文名称 */
  nameEn?: string
  /** 启用状态：0-禁用，1-启用（默认1） */
  enable?: number
  /** 节点列表 */
  nodes: TrackingNodeRequest[]
}

/**
 * 供应商填写字段配置
 */
export interface SupplierInputFieldConfig {
  /** 字段编码 */
  fieldCode: string
  /** 字段名称 */
  fieldName: string
  /** 字段类型 */
  fieldType: 'status' | 'exception_group' | 'datetime' | 'text' | 'file'
  /** 是否必填 */
  required: boolean
  /** 是否显示 */
  visible: boolean
  /** 是否锁定（系统字段不可配置） */
  locked: boolean
}

/**
 * 节点请求参数
 */
export interface TrackingNodeRequest {
  /** 节点ID（更新时必填，新增时不填） */
  id?: string
  /** 节点编码 */
  code: string
  /** 节点中文名称 */
  nameCn: string
  /** 节点英文名称 */
  nameEn?: string
  /** 启用状态：0-禁用，1-启用（默认1） */
  enable?: number
  /** 是否外部展示：0-否，1-是（默认0） */
  externalDisplay?: number
  /** 文件ID（节点图标） */
  fileId?: string
  /** 排序号（默认0） */
  sort?: number
  /** 阶段编码（自由文本；同 stage 的节点会被分到同一组渲染） */
  stage?: string
  /** 阶段中文名称（如「起运地（出口）」） */
  stageNameCn?: string
  /** 是否启用供应商填写 */
  supplierInputEnabled?: boolean
  /** 供应商填写字段配置 */
  supplierInputFields?: SupplierInputFieldConfig[]
}

/**
 * 保存运踪配置请求参数
 */
export interface TrackingConfigSaveRequest {
  /** 渠道信息 */
  channel: TrackingChannelRequest
  /** 业务列表 */
  businesses: TrackingBusinessRequest[]
}

/**
 * 节点VO
 */
export interface TrackingNodeVO {
  /** 节点ID */
  id: string
  /** 渠道ID */
  channelId: string
  /** 业务ID */
  businessId: string
  /** 节点编码 */
  code: string
  /** 节点中文名称 */
  nameCn: string
  /** 节点英文名称 */
  nameEn?: string
  /** 启用状态：0-禁用，1-启用 */
  enable: number
  /** 是否外部展示：0-否，1-是 */
  externalDisplay: number
  /** 文件ID（节点图标） */
  fileId?: string
  /** 排序号 */
  sort: number
  /** 阶段编码 */
  stage?: string
  /** 阶段中文名称 */
  stageNameCn?: string
  /** 是否启用供应商填写 */
  supplierInputEnabled?: boolean
  /** 供应商填写字段配置 */
  supplierInputFields?: SupplierInputFieldConfig[]
}

/**
 * 业务VO
 */
export interface TrackingBusinessVO {
  /** 业务ID */
  id: string
  /** 渠道ID */
  channelId: string
  /** 业务编码 */
  code: string
  /** 业务中文名称 */
  nameCn: string
  /** 业务英文名称 */
  nameEn?: string
  /** 启用状态：0-禁用，1-启用 */
  enable: number
  /** 节点列表 */
  nodes: TrackingNodeVO[]
}

/**
 * 节点统计VO
 */
export interface TrackingNodeStatisticsVO {
  /** 总节点数 */
  totalNodes: number
  /** 已启用节点数量 */
  enabledNodes: number
  /** 外部展示节点数量 */
  externalDisplayNodes: number
}

/**
 * 运踪配置VO
 */
export interface TrackingConfigVO {
  /** 渠道ID */
  id: string
  /** 渠道编码 */
  code: string
  /** 渠道中文名称 */
  nameCn: string
  /** 渠道英文名称 */
  nameEn?: string
  /** 渠道类型：0-主渠道，1-第三方渠道 */
  type: number
  /** 启用状态：0-禁用，1-启用 */
  enable: number
  /** 业务列表 */
  businesses: TrackingBusinessVO[]
  /** 节点统计数据 */
  statistics: TrackingNodeStatisticsVO
}

// ===================== 接口实现 =====================

/**
 * 保存运踪配置
 * @param params 保存参数
 * @param options 调用选项；silent=true 时关闭 http 拦截器的全局错误 toast，
 *               以便业务侧自行决定友好提示文案（参考 utils/http.ts 中的 showErrorMessage）
 * @returns 操作结果
 */
export function saveTrackingConfig(
  params: TrackingConfigSaveRequest,
  options?: { silent?: boolean }
): Promise<boolean> {
  const config = options?.silent ? { showErrorMessage: false } : undefined
  return request.post<boolean>('/sys/tracking/config/save', params, config as any)
}

/**
 * 查询运踪配置列表
 * @param params 查询参数
 * @returns 运踪配置列表
 */
export function queryTrackingConfigList(params: TrackingConfigQueryRequest = {}): Promise<TrackingConfigVO[]> {
  return request.post<TrackingConfigVO[]>('/sys/tracking/config/query', params)
}

/**
 * 根据业务查询当前业务的运踪节点信息（仅启用节点，按 sort 升序）
 * @param params channelCode + businessCode，韶音示例：shokz + OF
 */
export interface GetTrackingNodesRequest {
  /** 渠道编码（韶音默认 shokz） */
  channelCode: string
  /** 业务编码（AF/OF/IE/DE/DL/IL） */
  businessCode: string
}

export function getTrackingNodes(params: GetTrackingNodesRequest): Promise<TrackingNodeVO[]> {
  return request.post<TrackingNodeVO[]>('/sys/tracking/config/nodes/by-business', params)
}
